if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.sirv = null;
        this.mediaGallery = null;
        this.sliderComponent = null;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initMediaGallery();
        this.initSirvLazyLoad();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, variant } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        this.applyClientVariant(variant);

        const productUrl = target?.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const optionValues = (selectedOptionValues || []).filter(Boolean);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, optionValues, shouldFetchFullPage, variant?.id),
          targetId: target?.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, variant),
        });
      }

      applyClientVariant(variant) {
        if (!variant?.id) return;

        const mediaId = variant.featured_media_id || variant.featured_media?.id || variant.featured_image?.id;
        if (mediaId) {
          try {
            this.querySelector('media-gallery')?.setActiveMedia?.(`${this.dataset.section}-${mediaId}`, true);
          } catch (error) {
            console.error(error);
          }
        }

        this.updatePriceFromVariant(variant);
        this.updateVariantInputs(variant.id);

        document.dispatchEvent(
          new CustomEvent('product:variant-change', {
            bubbles: true,
            detail: { variant },
          })
        );
      }

      updatePriceFromVariant(variant) {
        const priceRoot =
          this.querySelector(`#price-${this.dataset.section}`) ||
          document.getElementById(`price-${this.dataset.section}`);
        if (!priceRoot) return;

        const salePrice = priceRoot.querySelector('.price-item--sale');
        const regularPrice = priceRoot.querySelector('.price-item--regular');
        const comparePrice = priceRoot.querySelector('.price-item--before-sale');
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

        if (salePrice && variant.price_formatted) salePrice.innerHTML = variant.price_formatted;
        if (regularPrice && variant.price_formatted) regularPrice.innerHTML = variant.price_formatted;
        if (comparePrice && variant.compare_at_price_formatted) {
          comparePrice.innerHTML = variant.compare_at_price_formatted;
        }

        priceRoot.querySelector('.price')?.classList.toggle('price--on-sale', Boolean(onSale));
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton?.(true);
        productForm?.handleErrorMessage?.();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          if (variant?.id) this.updateURL(productUrl, variant.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (!targetId) return;
            const focusTarget = document.getElementById(targetId);
            if (focusTarget && focusTarget.focus) {
              focusTarget.focus({ preventScroll: true });
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        try {
          const selectedVariant = productInfoNode
            ?.querySelector('variant-selects [data-selected-variant]')
            ?.textContent;
          return selectedVariant ? JSON.parse(selectedVariant) : null;
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
        const params = [];

        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);
        if (variantId) params.push(`variant=${variantId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects && this.variantSelectors) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, clientVariant) {
        return (html) => {
          const variant = this.getSelectedVariant(html) || clientVariant;

          try {
            this.pickupAvailability?.update(variant);
          } catch (error) {
            console.error(error);
          }

          try {
            this.updateOptionValues(html);
          } catch (error) {
            console.error(error);
          }

          if (variant?.id) {
            this.updateURL(productUrl, variant.id);
            this.updateVariantInputs(variant.id);
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          const mediaId = variant.featured_media?.id || variant.featured_media_id || variant.featured_image?.id;
          try {
            this.updateMedia(html, mediaId);
          } catch (error) {
            console.error(error);
          }

          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source =
              html.getElementById(`${id}-${this.sectionId}`) ||
              html.getElementById(`${id.charAt(0).toUpperCase()}${id.slice(1)}-${this.sectionId}`);
            const destination =
              this.querySelector(`#${id}-${this.dataset.section}`) ||
              this.querySelector(`#${id.charAt(0).toUpperCase()}${id.slice(1)}-${this.dataset.section}`) ||
              document.getElementById(`${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton?.(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: { variant },
              bubbles: true,
            })
          );
          document.dispatchEvent(
            new CustomEvent('product:variant-change', {
              detail: { variant },
              bubbles: true,
            })
          );
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton?.(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html?.querySelector('media-gallery ul');

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      initMediaGallery() {
        this.mediaGallery = this.querySelector('media-gallery');
        this.sliderComponent = this.querySelector('slider-component');
        
        if (!this.mediaGallery) return;

        this.setupSliderEvents();
      }

      setupSliderEvents() {
        if (!this.sliderComponent) return;
        const slider = this.sliderComponent.querySelector('.slider');
        if (!slider) return;

        const observer = new MutationObserver(() => {
          this.checkActiveSlide();
        });

        observer.observe(slider, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true
        });

        const thumbnailButtons = this.querySelectorAll('.thumbnail-list__item button');
        thumbnailButtons.forEach(button => {
          button.addEventListener('click', () => {
            setTimeout(() => this.checkActiveSlide(), 100);
          });
        });

        const navButtons = this.querySelectorAll('.product-media-slider-button');
        navButtons.forEach(button => {
          button.addEventListener('click', () => {
            setTimeout(() => this.checkActiveSlide(), 100);
          });
        });
      }

      checkActiveSlide() {
        const activeSlide = this.querySelector('.slider__slide.is-active, .slider__slide[aria-current="true"]');
        
        if (!activeSlide) return;

        if (activeSlide.classList.contains('360image') || activeSlide.hasAttribute('data-360-image')) {
          if (!this.sirv) {
            this.initSirv();
          }
        }
      }

      initSirvLazyLoad() {
        const has360Images = this.querySelectorAll('[data-360-image], [class~="360image"]').length > 0;
        if (has360Images) {
          this.addPreconnect();
        }
      }

      addPreconnect() {
        if (document.querySelector('link[href="https://scripts.sirv.com"]')) {
          return;
        }

        const preconnectScripts = document.createElement('link');
        preconnectScripts.rel = 'preconnect';
        preconnectScripts.href = 'https://scripts.sirv.com';
        preconnectScripts.crossOrigin = 'anonymous';
        document.head.appendChild(preconnectScripts);
        const first360Image = this.querySelector('[data-360-image], [class~="360image"]');
        if (first360Image) {
          const sirvDiv = first360Image.querySelector('[data-src], [data-id]');
          if (sirvDiv) {
            const dataSrc = sirvDiv.getAttribute('data-src');
            if (dataSrc) {
              try {
                const url = new URL(dataSrc);
                const preconnectCDN = document.createElement('link');
                preconnectCDN.rel = 'preconnect';
                preconnectCDN.href = `https://${url.hostname}`;
                preconnectCDN.crossOrigin = 'anonymous';
                document.head.appendChild(preconnectCDN);
              } catch (e) {}
            }
          }
        }
      }

      initSirv() {
        if (this.sirv) {
          return;
        }

        if (document.querySelector('script[src*="sirv.js"]')) {
          this.sirv = true;
          return;
        }

        const sirvScript = document.createElement('script');
        sirvScript.src = 'https://scripts.sirv.com/sirv.js';
        sirvScript.async = true;
        sirvScript.onload = () => {
          this.sirv = true;
          if (window.Sirv) {
            window.Sirv.init();
          }
        };
        document.head.appendChild(sirvScript);
        this.sirv = true;
      }
    }
  );
}
